import {useFocusWithin, useHover} from '@react-aria/interactions';
import cx from 'classnames';
import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import {FontVariant} from '../../../../design-tokens/constants';
import type {IconSource} from '../../../../design-tokens/generated/Icons';
import {Avatar, AvatarType} from '../../..';
import type {ButtonVariant} from '../../../types/variants';
import {TestId} from '../../../utils/testIds';
import {Button} from '../Button/Button';
import {Icon, IconTypes} from '../Icon/Icon';
import {InteractiveElement} from '../InteractiveElement/InteractiveElement';
import {Text} from '../Text/Text';

import styles from './Notification.module.scss';

export type NotificationItemId = string;

type NotificationCloseMillisecondsTimeout = number;

type NotificationCloseEpochMilliseconds = number;

export type NotificationButton = {
    onClick: (id: NotificationItemId) => void;
    text: string;
    variant: ButtonVariant;
    closeNotificationOnClick?: boolean;
};

export type NotificationItem<TGroup extends string = string> = {
    id: NotificationItemId;
    text: string | React.ReactElement;
    revision?: number;
    group?: TGroup; // An optional group that the notification belongs to.
    icon?: IconSource;
    avatarDisplayName?: string;
    isClosable?: boolean;
    closeTimeout?: NotificationCloseMillisecondsTimeout;
    closeAt?: NotificationCloseEpochMilliseconds;
    buttons?: NotificationButton[];
    onClick?: (id: NotificationItemId) => void;
};

enum NotificationState {
    Opening = 'opening',
    Open = 'open',
    Closing = 'closing',
    Closed = 'closed',
}

export const Notification = <TGroup extends string = string>({
    isClosable = false,
    size = 'medium',
    id,
    text,
    icon,
    avatarDisplayName,
    buttons,
    closeTimeout,
    closeAt,
    revision,
    handleClose,
    group,
    onClick,
}: NotificationItem<TGroup> & {
    size?: 'medium' | 'large';
    handleClose: (id: NotificationItemId, group?: TGroup) => void;
}) => {
    const [notificationState, setNotificationState] = useState(
        NotificationState.Closed,
    );
    const messageId = useId();
    const resetCloseDelayRef = useRef<number | undefined>(undefined);
    const closeTimeoutIdRef = useRef<number | undefined>(undefined);
    const isHoveredRef = useRef(false);
    const hasFocusWithinRef = useRef(false);
    const notificationUpdateRef = useRef({
        closeAt,
        closeTimeout,
        revision,
        version: 0,
    });
    const closingNotificationVersionRef = useRef<number | undefined>(undefined);

    const onClose = useCallback(() => {
        closingNotificationVersionRef.current =
            notificationUpdateRef.current.version;
        setNotificationState(NotificationState.Closing);
    }, []);

    const clearCloseTimer = useCallback(() => {
        window.clearTimeout(closeTimeoutIdRef.current);
        closeTimeoutIdRef.current = undefined;
    }, []);

    const startCloseTimer = useCallback(
        (closeDelay = resetCloseDelayRef.current) => {
            clearCloseTimer();
            if (closeDelay === undefined) {
                return;
            }
            closeTimeoutIdRef.current = window.setTimeout(onClose, closeDelay);
        },
        [clearCloseTimer, onClose],
    );

    const restartCloseTimerAfterInteraction = () => {
        if (!isHoveredRef.current && !hasFocusWithinRef.current) {
            startCloseTimer();
        }
    };

    const {hoverProps} = useHover({
        onHoverStart: () => {
            isHoveredRef.current = true;
            clearCloseTimer();
        },
        onHoverEnd: () => {
            isHoveredRef.current = false;
            restartCloseTimerAfterInteraction();
        },
    });
    const {focusWithinProps} = useFocusWithin({
        onFocusWithin: () => {
            hasFocusWithinRef.current = true;
            clearCloseTimer();
        },
        onBlurWithin: () => {
            hasFocusWithinRef.current = false;
            restartCloseTimerAfterInteraction();
        },
    });

    const buttonContent = buttons?.map(
        ({onClick, text, variant, closeNotificationOnClick}) => (
            <Button
                key={text}
                onClick={(event: React.SyntheticEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    onClick(id);
                    if (closeNotificationOnClick !== false) {
                        onClose();
                    }
                }}
                size={size}
                colorScheme="light"
                variant={variant}
            >
                {text}
            </Button>
        ),
    );

    useLayoutEffect(() => {
        const previousNotificationUpdate = notificationUpdateRef.current;
        const hasNotificationChanged =
            previousNotificationUpdate.closeAt !== closeAt ||
            previousNotificationUpdate.closeTimeout !== closeTimeout ||
            previousNotificationUpdate.revision !== revision;
        if (hasNotificationChanged) {
            notificationUpdateRef.current = {
                closeAt,
                closeTimeout,
                revision,
                version: previousNotificationUpdate.version + 1,
            };
            setNotificationState(currentState =>
                currentState === NotificationState.Closing
                    ? NotificationState.Open
                    : currentState,
            );
        }
        const initialCloseDelay =
            closeAt === undefined
                ? closeTimeout
                : Math.max(closeAt - Date.now(), 0);
        resetCloseDelayRef.current = closeTimeout ?? initialCloseDelay;
        if (!isHoveredRef.current && !hasFocusWithinRef.current) {
            startCloseTimer(initialCloseDelay);
        }

        return clearCloseTimer;
    }, [clearCloseTimer, closeAt, closeTimeout, revision, startCloseTimer]);

    useEffect(() => {
        setNotificationState(NotificationState.Opening);
    }, []);

    return (
        <div
            {...hoverProps}
            {...focusWithinProps}
            data-testid={TestId.Notification}
            onTransitionEnd={() => {
                switch (notificationState) {
                    case NotificationState.Opening:
                        return setNotificationState(NotificationState.Open);
                    case NotificationState.Closing:
                        if (
                            closingNotificationVersionRef.current !==
                            notificationUpdateRef.current.version
                        ) {
                            return;
                        }
                        handleClose(id, group);
                        return;
                }
            }}
            className={cx(styles.notification, {
                [styles.open]:
                    notificationState === NotificationState.Opening ||
                    notificationState === NotificationState.Open,
                [styles.close]: notificationState === NotificationState.Closing,
            })}
        >
            {onClick && (
                <InteractiveElement
                    data-testid={TestId.ButtonInteractiveNotification}
                    aria-labelledby={messageId}
                    className={styles.interactiveNotification}
                    focusClassName={styles.interactiveNotificationFocus}
                    htmlTag="button"
                    onClick={() => {
                        onClick(id);
                        onClose();
                    }}
                    type="button"
                />
            )}
            <div className={styles.contentAndCloseButtonContainer}>
                <div className={styles.content}>
                    <div className={styles.iconAndTextContainer}>
                        {icon && !avatarDisplayName && (
                            <Icon className={styles.icon} source={icon} />
                        )}
                        {avatarDisplayName && (
                            <Avatar
                                className={styles.avatar}
                                colorScheme="light"
                                displayName={avatarDisplayName}
                                sizeModifier="md"
                                type={AvatarType.Initials}
                            />
                        )}
                        <div className={styles.text}>
                            <span id={messageId}>
                                {typeof text === 'string' ? (
                                    <Text
                                        colorScheme="light"
                                        fontVariant={FontVariant.Body}
                                    >
                                        {text}
                                    </Text>
                                ) : (
                                    text
                                )}
                            </span>
                        </div>
                    </div>
                </div>
                {isClosable && (
                    <Button
                        className={styles.closeButton}
                        onClick={(
                            e: React.SyntheticEvent<HTMLButtonElement>,
                        ) => {
                            onClose();
                            e.stopPropagation();
                        }}
                        colorScheme="light"
                        size="small"
                        variant="transparent"
                        modifier="square"
                        aria-label="close notification"
                        data-testid={TestId.ButtonCloseNotification}
                    >
                        <Icon source={IconTypes.IconClose} size="small" />
                    </Button>
                )}
            </div>
            {buttonContent && (
                <div className={styles.buttonsContainer}>{buttonContent}</div>
            )}
        </div>
    );
};

export type NotificationProps = React.ComponentProps<typeof Notification>;
